// Timeline de envios (estilo log-timeline do Heimdall): area chart
// compacto com linha contínua do TOTAL de envios por bucket. O eixo X
// se adapta à granularidade (`interval` + `bucket` do backend): HORA
// (intradiário), DIA ou SEMANA (histórico). **No modo HORA, arrastar
// sobre a faixa seleciona o período** (estilo Kibana/Datadog) e aplica
// via setHourRange — o drag-select não faz sentido no histórico e fica
// desligado. O tooltip preserva o breakdown por macro-status do bucket.
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { formatNotificationBucketLabel } from "@/hooks/use-notification-center-dashboard-search-params"
import { cn } from "@/lib/utils"
import type {
  NotificationOverviewIntervalType,
  NotificationOverviewSeriesPointType,
} from "@/types/notification-center"

import {
  NOTIFICATION_MACRO_STATUS_CHART_COLORS,
  NOTIFICATION_MACRO_STATUS_SERIES_KEYS,
  NOTIFICATION_MACRO_STATUSES,
} from "../constants"
import { DashboardChartCard } from "./dashboard-chart-card"

interface DashboardSeriesChartProps {
  series?: NotificationOverviewSeriesPointType[]
  isLoading: boolean
  hasError: boolean
  onRetry: () => void
  // Granularidade do eixo X (hour/day/week). Default hour.
  interval?: NotificationOverviewIntervalType
  // Aplica a faixa de horas selecionada por arraste (from < to). Só é
  // acionado no modo HORA (o container ignora nos demais).
  onHourRangeSelect: (
    fromHour: string,
    toHour: string
  ) => void
}

/** "08:00" → 8; null quando o label não é uma hora válida. */
const parseHour = (value: string): number | null => {
  const match = /^(\d{2}):00$/.exec(value)
  return match ? Number(match[1]) : null
}

/**
 * Converte as duas extremidades do arrasto (labels "HH:00", em
 * qualquer ordem) na faixa from/to a aplicar: from = bucket mais
 * cedo; to = bucket mais tarde **+ 1 hora** (inclui a largura do
 * último bucket), com teto em 23:00 — o hook só aceita horas cheias
 * e exige from < to.
 */
export const computeHourBrushRange = (
  a: string,
  b: string
): { fromHour: string; toHour: string } | null => {
  const h1 = parseHour(a)
  const h2 = parseHour(b)
  if (h1 === null || h2 === null) return null
  const from = Math.min(h1, h2)
  const to = Math.min(Math.max(h1, h2) + 1, 23)
  if (from >= to) return null
  const pad = (hour: number) =>
    `${String(hour).padStart(2, "0")}:00`
  return { fromHour: pad(from), toHour: pad(to) }
}

/** Estado do recharts nos eventos de mouse (só o que usamos). */
type ChartMouseState =
  | { activeLabel?: string | number }
  | null
  | undefined

export const DashboardSeriesChart = ({
  series,
  isLoading,
  hasError,
  onRetry,
  interval = "hour",
  onHourRangeSelect,
}: DashboardSeriesChartProps) => {
  const { t } = useTranslation()
  const seriesPrefix =
    "pages.notificationCenter.dashboardPage.charts.series"
  const macroStatusPrefix =
    "pages.notificationCenter.searchPage.macroStatus"

  // Drag-select de horas só faz sentido no modo HORA (intradiário).
  const isHourly = interval === "hour"

  // Total por bucket = soma dos macro-status (cada envio está em
  // exatamente um estado).
  const data = (series ?? []).map((point) => ({
    ...point,
    // `bucket` vem sempre; fallback p/ `hour` por robustez.
    bucket: point.bucket ?? point.hour ?? "",
    total: NOTIFICATION_MACRO_STATUSES.reduce(
      (sum, macroStatus) =>
        sum +
        (point[
          NOTIFICATION_MACRO_STATUS_SERIES_KEYS[macroStatus]
        ] ?? 0),
      0
    ),
  }))
  const total = data.reduce(
    (sum, point) => sum + point.total,
    0
  )

  // Extremidades do arrasto em labels de bucket; o REF é a fonte da
  // verdade no mouseup (o state alimenta só o retângulo visual e
  // sofre lag de commit sob eventos rápidos).
  const brushRef = useRef<{
    start: string
    end: string | null
  } | null>(null)
  const [brush, setBrush] = useState<{
    start: string
    end: string | null
  } | null>(null)

  const handleMouseDown = (state: ChartMouseState) => {
    if (!isHourly || state?.activeLabel == null) return
    const next = {
      start: String(state.activeLabel),
      end: null,
    }
    brushRef.current = next
    setBrush(next)
  }

  const handleMouseMove = (state: ChartMouseState) => {
    if (
      !isHourly ||
      state?.activeLabel == null ||
      !brushRef.current
    )
      return
    brushRef.current = {
      ...brushRef.current,
      end: String(state.activeLabel),
    }
    setBrush(brushRef.current)
  }

  const handleMouseUp = (state: ChartMouseState) => {
    if (!isHourly) return
    const drag = brushRef.current
    const end =
      state?.activeLabel != null
        ? String(state.activeLabel)
        : drag?.end
    if (drag && drag.end != null && end != null) {
      const hourRange = computeHourBrushRange(
        drag.start,
        end
      )
      if (hourRange)
        onHourRangeSelect(
          hourRange.fromHour,
          hourRange.toHour
        )
    }
    brushRef.current = null
    setBrush(null)
  }

  const cancelBrush = () => {
    brushRef.current = null
    setBrush(null)
  }

  return (
    <DashboardChartCard
      title={t(`${seriesPrefix}.title`)}
      testId="dashboard-series"
      isLoading={isLoading}
      hasError={hasError}
      isEmpty={!series?.length}
      onRetry={onRetry}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold tabular-nums tracking-tight">
          {t(`${seriesPrefix}.totalCount`, {
            count: total,
          })}
        </p>
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {/* Dica de arraste só no modo HORA (drag-select ativo). */}
          {isHourly && (
            <>
              {t(`${seriesPrefix}.brushHint`)}
              {" · "}
            </>
          )}
          {t(`${seriesPrefix}.grain_${interval}`)}
        </span>
      </div>
      {/* Ciano (referência do log-timeline) em vez do primary do
          tema — a linha/área herdam via currentColor. */}
      <div
        className={cn(
          "select-none text-sky-500",
          isHourly && "cursor-crosshair"
        )}
      >
        {/* height NUMERICO: nao depende de medir o pai (o aspect do
            ChartContainer estourava a altura no card). */}
        <ResponsiveContainer width="100%" height={90}>
          <AreaChart
            data={data}
            margin={{
              top: 4,
              right: 4,
              bottom: 0,
              left: -20,
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={cancelBrush}
          >
            <defs>
              <linearGradient
                id="notificationSeriesFill"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor="currentColor"
                  stopOpacity={0.45}
                />
                <stop
                  offset="100%"
                  stopColor="currentColor"
                  stopOpacity={0.06}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              stroke="hsl(var(--border))"
              strokeOpacity={0.6}
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="bucket"
              // Rótulo amigável por granularidade (HH:00 / dd/MM / Sxx).
              tickFormatter={(value: string) =>
                formatNotificationBucketLabel(
                  value,
                  interval
                )
              }
              tick={{ fontSize: 10 }}
              className="tabular-nums text-muted-foreground"
              stroke="currentColor"
              tickLine={false}
              axisLine={false}
              minTickGap={32}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              className="tabular-nums text-muted-foreground"
              stroke="currentColor"
              tickLine={false}
              axisLine={false}
              width={32}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{
                fill: "currentColor",
                fillOpacity: 0.06,
              }}
              contentStyle={{
                background: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 8,
                fontSize: 12,
                padding: "6px 9px",
              }}
              // Uma linha por macro-status: o tooltip lista cada
              // série; o formatter só traduz a chave para o rótulo.
              formatter={(value: number, name: string) => {
                const macroStatus =
                  NOTIFICATION_MACRO_STATUSES.find(
                    (status) =>
                      NOTIFICATION_MACRO_STATUS_SERIES_KEYS[
                        status
                      ] === name
                  )
                return [
                  value,
                  macroStatus
                    ? t(
                        `${macroStatusPrefix}.${macroStatus}`
                      )
                    : name,
                ]
              }}
            />
            {/* Retângulo da seleção enquanto arrasta. */}
            {brush?.end != null &&
            brush.end !== brush.start ? (
              <ReferenceArea
                x1={brush.start}
                x2={brush.end}
                fill="currentColor"
                fillOpacity={0.12}
                stroke="currentColor"
                strokeOpacity={0.4}
                isFront
              />
            ) : null}
            {NOTIFICATION_MACRO_STATUSES.filter(
              (macroStatus) =>
                data.some(
                  (point) =>
                    (point[
                      NOTIFICATION_MACRO_STATUS_SERIES_KEYS[
                        macroStatus
                      ]
                    ] ?? 0) > 0
                )
            ).map((macroStatus) => (
              <Area
                key={macroStatus}
                type="monotone"
                dataKey={
                  NOTIFICATION_MACRO_STATUS_SERIES_KEYS[
                    macroStatus
                  ]
                }
                stroke={
                  NOTIFICATION_MACRO_STATUS_CHART_COLORS[
                    macroStatus
                  ]
                }
                strokeWidth={2}
                fill={
                  NOTIFICATION_MACRO_STATUS_CHART_COLORS[
                    macroStatus
                  ]
                }
                fillOpacity={0.06}
                dot={false}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Legenda compacta: cor de cada tipo presente no dia. */}
      <div className="mt-2 flex flex-wrap gap-3">
        {NOTIFICATION_MACRO_STATUSES.filter((macroStatus) =>
          data.some(
            (point) =>
              (point[
                NOTIFICATION_MACRO_STATUS_SERIES_KEYS[
                  macroStatus
                ]
              ] ?? 0) > 0
          )
        ).map((macroStatus) => (
          <span
            key={macroStatus}
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <span
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full"
              style={{
                backgroundColor:
                  NOTIFICATION_MACRO_STATUS_CHART_COLORS[
                    macroStatus
                  ],
              }}
            />
            {t(`${macroStatusPrefix}.${macroStatus}`)}
          </span>
        ))}
      </div>
    </DashboardChartCard>
  )
}
